import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "lazy-retry-reloaded";

/**
 * lazy() wrapper that recovers from stale chunk errors after a redeploy.
 * If a dynamic import fails (typically because the referenced chunk hash no
 * longer exists on the server), we force a one-time hard reload to pick up
 * the new index.html + chunk manifest.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Successful import — clear the guard so future failures can reload again.
      try { window.sessionStorage.removeItem(RELOAD_KEY); } catch { /* ignore */ }
      return mod;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isChunkError =
        /Importing a module script failed|Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk \d+ failed/i.test(
          message,
        );
      if (!isChunkError) throw err;

      let alreadyReloaded = false;
      try {
        alreadyReloaded = window.sessionStorage.getItem(RELOAD_KEY) === "1";
        window.sessionStorage.setItem(RELOAD_KEY, "1");
      } catch { /* ignore */ }

      if (!alreadyReloaded) {
        window.location.reload();
        // Return a never-resolving promise so Suspense keeps the fallback
        // shown until the reload swaps the page out.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
